/* ============================================================
   PHREED — Scroll, motion, and interaction (v5)
   ============================================================
   Modules (each in its own IIFE so a single failure doesn't
   break the others):
   1. Section reveal (IntersectionObserver)
   2. Count-up for ingredient doses (data-countup attribute)
   3. Hero parallax (desktop only)
   4. Desktop sticky bar (slides down after hero)
   ============================================================ */

(function reduceMotionEarlyExitHelper() {
  // No-op — each module checks reduced motion individually so users
  // get the right subset of behaviours.
})();

/* ============================================================
   1. SECTION REVEAL
   ------------------------------------------------------------
   `.phreed-reveal` starts at opacity:0 and gets `.revealed`
   added by the IntersectionObserver as it scrolls into view.
   Without that class, content stays invisible.

   THEME-EDITOR BUG FIX: when a merchant edits a setting in the
   Customizer, Shopify replaces the section's DOM with fresh
   markup. The previously-observed elements no longer exist;
   the new ones aren't observed by the original observer; they
   stay at opacity:0 and the section appears to "disappear" in
   the editor preview.
   Solution: also listen for `shopify:section:load` (and
   `:select`) and force-reveal `.phreed-reveal` elements inside
   the re-rendered section immediately — no scroll required,
   since the editor can't reliably simulate the scroll threshold.
   ============================================================ */
(function phreedSectionReveal() {
  function revealAll(root) {
    (root || document).querySelectorAll('.phreed-reveal').forEach(function(el) {
      el.classList.add('revealed');
    });
  }

  function observeElements(elements) {
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -30px 0px' });

    Array.prototype.forEach.call(elements, function(el) {
      observer.observe(el);
    });
  }

  /* PDP auto-classifier — adds `.phreed-reveal` (and stagger delay
     classes) to common PDP section elements at runtime, so the PDP
     gets the same fade-up scroll animation the home page uses,
     without having to touch every section's Liquid markup.
     Runs FIRST inside init() so the elements have the class before
     the observer scans for them below. */
  function autoClassifyPdp() {
    var pdp = document.querySelector('.phreed-pdp');
    if (!pdp) return;

    function addReveal(el, delayClass) {
      if (!el) return;
      if (el.classList.contains('phreed-reveal')) return;
      el.classList.add('phreed-reveal');
      if (delayClass) el.classList.add(delayClass);
    }

    /* Section-level intro trios — eyebrow → heading → subhead,
       staggered so the trio reads in as a sequence rather than
       all-at-once. Targets the FIRST occurrence in each section
       so nested elements (e.g. accordion summaries, card titles)
       aren't grabbed. */
    pdp.querySelectorAll('.phreed-section').forEach(function(section) {
      addReveal(section.querySelector('.phreed-eyebrow'));
      addReveal(section.querySelector('.phreed-h2'), 'phreed-delay-1');
      addReveal(section.querySelector('.phreed-section__intro'), 'phreed-delay-2');
    });

    /* Repeating cards — stagger by position within their list.
       Capped at delay-N so a long list (e.g. 11 ingredients or
       8 FAQs) doesn't have absurd delays on the last items. */
    function staggerCards(selector, maxDelay) {
      pdp.querySelectorAll(selector).forEach(function(card, index) {
        var delayIndex = Math.min(index + 1, maxDelay || 4);
        addReveal(card, 'phreed-delay-' + delayIndex);
      });
    }

    staggerCards('.phreed-problem__moment', 3);
    staggerCards('.phreed-why-failed__item', 4);
    staggerCards('.phreed-phase', 3);
    staggerCards('.phreed-mechanism__step', 3);
    staggerCards('.phreed-how-to-use__step', 3);
    staggerCards('.phreed-review', 5);

    /* Editorial image frames fade in as they scroll into view —
       matches the photo-anchor treatment on the home page. */
    pdp.querySelectorAll(
      '.phreed-problem__media,' +
      '.phreed-comparison__media,' +
      '.phreed-ingredients__media,' +
      '.phreed-image-frame--section-hero,' +
      '.phreed-image-frame--problem-hero,' +
      '.phreed-image-frame--founder,' +
      '.phreed-subscription__lifestyle,' +
      '.phreed-mechanism__step-image'
    ).forEach(function(img) {
      addReveal(img);
    });

    /* Payoff card in Problem section — the section close-out
       deserves its own fade-in beat, after the moments. */
    pdp.querySelectorAll('.phreed-problem__payoff').forEach(function(el) {
      addReveal(el, 'phreed-delay-2');
    });
  }

  function init() {
    /* Add reveal classes to PDP elements FIRST, before the observer
       scans — so newly-classified elements get observed. */
    autoClassifyPdp();

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    observeElements(document.querySelectorAll('.phreed-reveal'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme-editor recovery: re-classify AND re-reveal whenever a
  // section reloads. Re-classify first so newly-inserted PDP
  // elements get the reveal classes; then force-reveal immediately
  // (no observer) because the editor doesn't reliably simulate the
  // scroll position that would trip the IntersectionObserver
  // threshold.
  document.addEventListener('shopify:section:load', function(event) {
    autoClassifyPdp();
    if (event.target) revealAll(event.target);
  });

  document.addEventListener('shopify:section:select', function(event) {
    autoClassifyPdp();
    if (event.target) revealAll(event.target);
  });
})();

/* ============================================================
   2. COUNT-UP for ingredient doses
   Trigger: any element with data-countup="<value>"
   Examples that work cleanly:
     "10,000 SKB"   → counts 0 → 10,000, suffix " SKB"
     "2 Billion CFU" → counts 0 → 2, suffix " Billion CFU"
     "300 GALU"     → counts 0 → 300, suffix " GALU"
     "100mg"        → counts 0 → 100, suffix "mg"
   Doses with em-dashes or compound text (e.g. "300mg — equivalent
   to 9,000mg cinnamon") are skipped — they parse to the first
   number only and the rest displays static.
   ============================================================ */
(function phreedCountUp() {
  function init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var elements = document.querySelectorAll('[data-countup]');
    if (!elements.length) return;

    function parseTarget(text) {
      // Match leading number (with commas/decimal) then capture the rest as suffix.
      var match = text.match(/^([\d,]+(?:\.\d+)?)(.*)$/);
      if (!match) return null;
      var numStr = match[1].replace(/,/g, '');
      var num = parseFloat(numStr);
      if (isNaN(num)) return null;
      return {
        number: num,
        suffix: match[2] || '',
        useCommas: match[1].indexOf(',') > -1,
        decimals: (numStr.split('.')[1] || '').length,
        original: text
      };
    }

    function formatNumber(n, parsed) {
      var rounded;
      if (parsed.decimals > 0) {
        rounded = n.toFixed(parsed.decimals);
      } else {
        rounded = Math.round(n);
      }
      if (parsed.useCommas) {
        var parts = String(rounded).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        rounded = parts.join('.');
      }
      return rounded + parsed.suffix;
    }

    function animateCount(el) {
      var raw = el.getAttribute('data-countup');
      var parsed = parseTarget(raw);
      if (!parsed) return;

      var duration = 1400;
      var startTime = null;
      var initialText = el.textContent;

      function step(ts) {
        if (!startTime) startTime = ts;
        var progress = Math.min((ts - startTime) / duration, 1);
        // ease-out cubic
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = parsed.number * eased;
        el.textContent = formatNumber(current, parsed);
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          // Snap to the original string so any non-numeric formatting
          // (e.g. "9,000mg cinnamon") is preserved.
          el.textContent = parsed.original;
        }
      }

      // Start at 0 immediately so the user doesn't see the final value flash.
      el.textContent = formatNumber(0, parsed);
      requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) {
      elements.forEach(animateCount);
      return;
    }

    var countObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          countObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    elements.forEach(function(el) {
      countObserver.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   3. HERO PARALLAX — desktop only
   The hero capsule image translates Y as the page scrolls, at a
   slower rate than the page itself. Subtle (-20px to +20px range).
   ============================================================ */
(function phreedHeroParallax() {
  function init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(max-width: 860px)').matches) return;

    // Try common selectors for the hero image
    var heroImg = document.querySelector('.phreed-hero .phreed-gallery__media--active') ||
                  document.querySelector('.phreed-hero__image') ||
                  document.querySelector('.phreed-hero img');
    if (!heroImg) return;

    var ticking = false;

    function updateParallax() {
      var rect = heroImg.getBoundingClientRect();
      var viewportHeight = window.innerHeight;

      // Skip if completely off-screen
      if (rect.bottom < 0 || rect.top > viewportHeight) {
        ticking = false;
        return;
      }

      // Progress: -1 (just entered top) → 0 (centred) → +1 (about to leave bottom)
      var center = rect.top + rect.height / 2;
      var progress = (center - viewportHeight / 2) / (viewportHeight / 2 + rect.height / 2);
      // Clamp
      if (progress > 1) progress = 1;
      if (progress < -1) progress = -1;

      var translateY = -progress * 20; // -20px to +20px
      heroImg.style.transform = 'translateY(' + translateY.toFixed(1) + 'px)';
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   4. DESKTOP STICKY BAR — slides down once user scrolls past hero
   ============================================================ */
(function phreedStickyBar() {
  function init() {
    var bar = document.querySelector('[data-sticky-bar]');
    if (!bar) return;
    if (window.matchMedia('(max-width: 860px)').matches) return;

    var hero = document.querySelector('.phreed-hero');
    if (!hero) return;

    var heroBottom = 0;
    var ticking = false;

    function recalc() {
      heroBottom = hero.getBoundingClientRect().bottom + window.scrollY;
    }

    function checkVisibility() {
      if (window.scrollY > heroBottom - 80) {
        bar.classList.add('is-visible');
      } else {
        bar.classList.remove('is-visible');
      }
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(checkVisibility);
        ticking = true;
      }
    }

    window.addEventListener('resize', function() {
      recalc();
      checkVisibility();
    }, { passive: true });

    window.addEventListener('scroll', onScroll, { passive: true });

    recalc();
    checkVisibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();